import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";

interface AuthState {
  user: User | null;
  session: Session | null;
  orgId: string | null;
  isPlatformOwner: boolean;
  loading: boolean;
  error: string | null;
}

/**
 * Centralized authentication and organization fetching hook
 * Uses proper session management to prevent logout loops
 */
export const useAuth = () => {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    orgId: null,
    isPlatformOwner: false,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let isMounted = true;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        
        // Update session and user synchronously
        setState(prev => ({
          ...prev,
          session,
          user: session?.user ?? null,
          loading: !session ? false : prev.loading,
          error: !session ? null : prev.error,
        }));

        // If we have a session, fetch org data with setTimeout to prevent deadlock
        if (session?.user) {
          setTimeout(() => {
            fetchOrgData(session.user.id);
          }, 0);
        } else {
          setState(prev => ({
            ...prev,
            orgId: null,
            isPlatformOwner: false,
            loading: false,
          }));
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      
      setState(prev => ({
        ...prev,
        session,
        user: session?.user ?? null,
      }));

      if (session?.user) {
        fetchOrgData(session.user.id);
      } else {
        setState(prev => ({
          ...prev,
          loading: false,
        }));
      }
    });

    const fetchOrgData = async (userId: string) => {
      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('org_id')
          .eq('id', userId)
          .single();

        if (profileError && profileError.code !== 'PGRST116') {
          console.error("Profile fetch error:", profileError);
        }

        // Check if user is platform owner (has owner role with NULL org_id)
        const { data: platformOwnerRole } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .eq('role', 'owner')
          .is('org_id', null)
          .single();

        if (isMounted) {
          setState(prev => ({
            ...prev,
            orgId: profile?.org_id || null,
            isPlatformOwner: !!platformOwnerRole,
            loading: false,
            error: null,
          }));
        }
      } catch (err: any) {
        console.error("Org data fetch error:", err);
        if (isMounted) {
          setState(prev => ({
            ...prev,
            loading: false,
            error: err.message,
          }));
        }
      }
    };

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return state;
};
